import { useAppState } from "contexts/UserContext";
import { useTeam } from "contexts/TeamContext";
import { useNavigate, useLocation } from "react-router-dom";
import React, { useState, useCallback } from "react";
import type { Team } from "../types";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { MoveLeft, Plus } from "lucide-react";
import { VaulDrawerShell } from "../../../components/ui/drawer/VaulDrawerShell";
import AddTeam from "../add-team/AddTeam";
import TeamList from "./TeamList";
import TeamMembersList from "../components/TeamMembersList";
import SubscribeModal from "./SubscribeModal";
import { useFreeAccountCheck } from "@/features/home/hooks/useFreeAccountCheck";
import { useAuth } from "@/contexts/AuthContext";

const TeamsOverview = () => {
  const { state } = useLocation();
  const { isSubscribedStripe } = useAppState();
  const { teams } = useTeam();
  const { userId } = useAuth();
  const { isFreeAccount } = useFreeAccountCheck({userId})
  const [showAddTeamModal, setShowAddTeamModal] = useState(false);
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "",
  });

  const hasStripeSubscription =
    typeof state?.hasStripeSubscription === "boolean"
      ? state.hasStripeSubscription
      : isSubscribedStripe;

  const navigate = useNavigate();

  const handleAddTeamDrawerChange = useCallback((nextOpen: boolean) => {
    setShowAddTeamModal(nextOpen);
  }, []);

  const handleSubscribeDrawerChange = useCallback((nextOpen: boolean) => {
    setShowSubscribeModal(nextOpen);
  }, []);

  return (
    <div className="root">
      <div className="settings-element-header">
        <MoveLeft
          onClick={() => navigate("/app/settings")}
          style={{ marginLeft: "5px", marginRight: "5px" }}
        />
        <p className={"settings-element-title page-title"}>Teams</p>

        <div></div>
        <Plus
          style={{ marginLeft: "5px", marginRight: "5px" }}
          onClick={() => {
            if (hasStripeSubscription || isFreeAccount || true) {
              setShowAddTeamModal(true);
            } else {
              setShowSubscribeModal(true);
            }
          }}
        />
      </div>

      {notification.show && (
        <div
          className={`notification-enter team-settings-notification ${
            notification.type === "error"
              ? "team-settings-notification--error"
              : "team-settings-notification--success"
          }`}
        >
          <div className="team-settings-notification-icon">
            {notification.type === "error" ? "⚠️" : "✅"}
          </div>

          <div className="team-settings-notification-message">
            <span className="team-settings-notification-title">
              {notification.type === "error" ? "Error" : "Success"}
            </span>
            <span className="team-settings-notification-text">
              {notification.message}
            </span>
          </div>

          <button
            onClick={() =>
              setNotification({ show: false, message: "", type: "" })
            }
            className="team-settings-notification-close"
          >
            ×
          </button>
        </div>
      )}

      {showAddTeamModal && (
        <VaulDrawerShell
          open={showAddTeamModal}
          onOpenChange={handleAddTeamDrawerChange}
        >
          <AddTeam onClose={() => setShowAddTeamModal(false)} />
        </VaulDrawerShell>
      )}

      {showSubscribeModal && (
        <VaulDrawerShell
          open={showSubscribeModal}
          onOpenChange={handleSubscribeDrawerChange}
        >
          <SubscribeModal onClose={() => setShowSubscribeModal(false)} />
        </VaulDrawerShell>
      )}

      {selectedTeam ? <TeamMembersList /> : null}

      <div>
        <TeamList
          teams={teams}
          onRowClick={(teamRow: Team) => {
            setSelectedTeam(teamRow as Team);
            navigate("/app/teamdetails", { state: { team: teamRow } });
          }}
        />
      </div>
    </div>
  );
};

export default TeamsOverview;
